import { useEffect, useState } from 'react'

import { Form, Input, Select, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'

import CommonModal from '@/components/common/CommonModal'
import Info from '@/components/common/Info'

import { ActionProps, ActionTypes, StringSplitAction, StringSplitSpecs } from './types'

type Direction = 'before' | 'after'

export type FormValues = {
  delimiter: string
  direction: Direction
}

// "Text Partition" (issue #697): keep the text on one side of a separator.
//   Before -> everything before the FIRST separator, separator kept
//             ("Q1a. Which of these?" -> "Q1a.")  = segment 0, keep_delimiter
//   After  -> everything after the LAST separator, separator dropped
//             ("Q1a. Which of these?" -> "Which of these?") = segment -1
// Whitespace is stripped from the result. The backend action type stays
// `string_split` (see transformations.py); only the UI is "Text Partition".
const directionToSpecs = (delimiter: string, direction: Direction): StringSplitSpecs =>
  direction === 'before'
    ? { delimiter, segment: 0, keep_delimiter: true, strip: true }
    : { delimiter, segment: -1, keep_delimiter: false, strip: true }

const specsToDirection = (specs: Partial<StringSplitSpecs>): Direction =>
  typeof specs.segment === 'number' && specs.segment < 0 ? 'after' : 'before'

const StringSplit = ({
  onSubmit,
  onCancel,
  isVisible,
  initialValues
}: ActionProps<StringSplitAction>) => {
  const initialSpecs = (initialValues?.specs || {}) as Partial<StringSplitSpecs>
  const initial: FormValues = {
    delimiter: initialSpecs.delimiter ?? '',
    direction: specsToDirection(initialSpecs)
  }
  const [form] = useForm<FormValues>()
  const [delimiter, setDelimiter] = useState(initial.delimiter)

  useEffect(() => {
    form.resetFields()
    setDelimiter(initial.delimiter)
    form.setFieldsValue(initial)
  }, [initialValues, isVisible])

  const submitForm = () => {
    form.submit()
    const { delimiter: submittedDelimiter, direction } = form.getFieldsValue(true)

    // actionType and type have the same meaning but are used for different
    // objects in the backend under different field names.
    const payload = {
      actionType: ActionTypes.STRING_SPLIT,
      type: ActionTypes.STRING_SPLIT,
      specs: directionToSpecs(submittedDelimiter, direction)
    }
    onSubmit(payload)
  }

  const popoverContent = (
    <div>
      <strong>Function</strong>
      <p>Keeps only part of the mapped text, split at the separator you choose.</p>
      <strong>How to use</strong>
      <p>[Separator]: The character (or text) to split at.</p>
      <p>
        [Search direction]: <em>Before</em> keeps everything up to and including the first
        separator (<em>&quot;Q1a. Which of these?&quot;</em> becomes
        <em> &quot;Q1a.&quot;</em>). <em>After</em> keeps everything following the last
        separator (<em>&quot;Which of these?&quot;</em>).
      </p>
      <strong>NOTES</strong>
      <p>
        <em>
          Surrounding whitespace is removed. If the separator is not found in the value, the
          text is left unchanged.
        </em>
      </p>
    </div>
  )

  return (
    <CommonModal
      visible={isVisible}
      onCancel={onCancel}
      onOk={submitForm}
      okDisabled={!delimiter}
      title={
        <span>
          Text Partition <Info>{popoverContent}</Info>
        </span>
      }
    >
      <Form form={form} initialValues={initial} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item name="delimiter" label="Separator" rules={[{ required: true }]}>
            <Input
              placeholder="e.g. ."
              maxLength={20}
              onChange={e => setDelimiter(e.target.value)}
            />
          </Form.Item>
          <Form.Item name="direction" label="Search direction">
            <Select
              options={[
                { value: 'before', label: 'Before — text before the first separator' },
                { value: 'after', label: 'After — text after the last separator' }
              ]}
            />
          </Form.Item>
        </Space>
      </Form>
    </CommonModal>
  )
}

export default StringSplit
